import "@fontsource/ibm-plex-sans-kr/400.css";
import "@fontsource/ibm-plex-sans-kr/500.css";
import "@fontsource/ibm-plex-sans-kr/600.css";
import "@fontsource/ibm-plex-mono/400.css";
import "./styles/app.css";
import { StrictMode, type ReactNode } from "react";
import { createRoot } from "react-dom/client";
import { createBrowserRouter, Navigate, RouterProvider, useLocation } from "react-router";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ApiError } from "./api/http";
import { useBootstrap } from "./api/queries";
import { ConfirmProvider, ToastProvider } from "./components/ui";
import { applyTheme, storedTheme } from "./lib/theme";
import { InvitePage, LoginPage, SetupPage } from "./pages/AuthPages";
import { ChannelPage, ThreadRoute } from "./pages/ChannelPage";
import { DMPage } from "./pages/DMPage";
import { BrowseChannelsPage, MembersPage, NotificationsPage, SettingsPage, ThreadsPage } from "./pages/OtherPages";
import { HomeRedirect, Workspace } from "./pages/Workspace";
import { WikiTemplatesPage } from "./pages/WikiTemplatesPage";
import { DraftsPage } from "./pages/DraftsPage";
import { SearchPage } from "./pages/SearchPage";
import { FilterPage, InboxPage, LabelPage, LabelsPage, ListPage, TodayPage, UpcomingPage } from "./pages/TaskPages";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      // 401은 다시 시도해도 소용없다
      retry: (count, err) => !(err instanceof ApiError && (err.status === 401 || err.status === 403 || err.status === 404)) && count < 2,
    },
  },
});

/** 설정 전이면 /setup, 로그인 전이면 /login */
function RequireAuth({ children }: { children: ReactNode }) {
  const boot = useBootstrap();
  const location = useLocation();
  if (boot.isPending) return <div className="splash">불러오는 중…</div>;
  if (boot.isError) return <div className="splash">서버에 연결할 수 없습니다. 잠시 후 새로고침하세요.</div>;
  if (boot.data.needsSetup) return <Navigate to="/setup" replace />;
  if (!boot.data.me) return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  return children;
}

/** 위키·보드는 따로 받는다 (편집기·끌어 놓기 라이브러리가 커서). 경로에 들어갈 때 받고, 첫 화면 뒤 한가할 때 미리 받아 둔다 */
type WikiModule = typeof import("./pages/wiki");
const loadWiki = () => import("./pages/wiki");
const loadBoard = () => import("./pages/BoardPage");
const wikiRoute = (name: Exclude<keyof WikiModule, "WikiPrintPage">) => async () => ({ Component: (await loadWiki())[name] });

const wikiChildren = [
  { index: true, lazy: wikiRoute("WikiHome") },
  { path: "log", lazy: wikiRoute("WikiLogPage") },
  { path: ":pageId", lazy: wikiRoute("WikiPageView") },
];

const router = createBrowserRouter([
  { path: "/setup", element: <SetupPage /> },
  { path: "/login", element: <LoginPage /> },
  { path: "/invite/:token", element: <InvitePage /> },
  {
    path: "/print/wiki/:pageId",
    lazy: async () => {
      const { WikiPrintPage } = await loadWiki();
      return {
        Component: () => (
          <RequireAuth>
            <WikiPrintPage />
          </RequireAuth>
        ),
      };
    },
  },
  {
    path: "/",
    element: (
      <RequireAuth>
        <Workspace />
      </RequireAuth>
    ),
    children: [
      { index: true, element: <HomeRedirect /> },
      { path: "c/:channelId", element: <ChannelPage />, children: [{ path: "t/:rootId", element: <ThreadRoute /> }] },
      { path: "c/:channelId/board", lazy: async () => ({ Component: (await loadBoard()).BoardPage }) },
      { path: "c/:channelId/wiki", lazy: wikiRoute("WikiLayout"), children: wikiChildren },
      { path: "wiki", lazy: wikiRoute("WikiLayout"), children: wikiChildren },
      { path: "admin/wiki-purge", lazy: wikiRoute("WikiPurgePage") },
      { path: "admin/wiki-templates", element: <WikiTemplatesPage /> },
      { path: "search", element: <SearchPage /> },
      { path: "inbox", element: <InboxPage /> },
      { path: "today", element: <TodayPage /> },
      { path: "upcoming", element: <UpcomingPage /> },
      { path: "lists/:listId", element: <ListPage /> },
      { path: "labels", element: <LabelsPage /> },
      { path: "labels/:labelId", element: <LabelPage /> },
      { path: "filters/:filterId", element: <FilterPage /> },
      { path: "d/:conversationId", element: <DMPage />, children: [{ path: "t/:rootId", element: <ThreadRoute /> }] },
      { path: "threads", element: <ThreadsPage /> },
      { path: "drafts", element: <DraftsPage /> },
      { path: "notifications", element: <NotificationsPage /> },
      { path: "channels", element: <BrowseChannelsPage /> },
      { path: "admin/members", element: <MembersPage /> },
      { path: "settings", element: <SettingsPage /> },
      { path: "*", element: <Navigate to="/" replace /> },
    ],
  },
]);

// 기기에 저장된 테마 (로그인하면 계정 설정으로 맞춘다)
applyTheme(storedTheme());

createRoot(document.getElementById("root")!).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <ToastProvider>
        <ConfirmProvider>
          <RouterProvider router={router} />
        </ConfirmProvider>
      </ToastProvider>
    </QueryClientProvider>
  </StrictMode>,
);

// 첫 화면을 그린 뒤 한가할 때 위키·보드 묶음을 미리 받아 둔다 (옮겨 갈 때 기다리지 않게)
const prefetchRoutes = () => void Promise.all([loadWiki(), loadBoard()]).catch(() => {});
if ("requestIdleCallback" in window) requestIdleCallback(prefetchRoutes, { timeout: 5000 });
else setTimeout(prefetchRoutes, 3000);

// 개발 중에도 등록한다 (웹 푸시 확인용). 탐색은 네트워크 우선이라 개발에 지장이 없다.
if ("serviceWorker" in navigator) {
  window.addEventListener("load", () => void navigator.serviceWorker.register("/sw.js"));
}
